/**
 * Tabs controller for the updated markup
 *
 * - Uses your class names:
 *   - Tablist: .tx-tabs
 *   - Tabs (buttons): .tx-btn_tab (active class: .tx__active)
 *   - Panels: .tx-tab_content
 * - ARIA-compliant, keyboard-friendly, hash-sync (#technical_data, etc.)
 * - Safe for multiple .tx-sp_meta groups on the page
 */
document.addEventListener('DOMContentLoaded', () => {
  /**
   * Initialize one tabs group
   * @param {HTMLElement} root
   * @returns {void}
   */
  const initTabs = (root) => {
    const tablist = root.querySelector('.tx-tabs[role="tablist"]') || root.querySelector('.tx-tabs');
    if (!tablist) return;

    const tabs = Array.from(tablist.querySelectorAll('.tx-btn_tab'));
    const panels = Array.from(root.querySelectorAll('.tx-tab_content'));

    // Ensure semantics
    tabs.forEach((tab) => tab.setAttribute('role', 'tab'));
    panels.forEach((panel) => panel.setAttribute('role', 'tabpanel'));

    const panelFor = (tab) => {
      const id = tab.getAttribute('aria-controls');
      return id ? root.querySelector(`#${CSS.escape(id)}`) : null;
    };

    /**
     * Activate specific tab
     * @param {HTMLButtonElement} tab
     * @param {{focus?: boolean, updateHash?: boolean}} opts
     * @returns {void}
     */
    const activate = (tab, { focus = true, updateHash = true } = {}) => {
      tabs.forEach((t) => {
        const selected = t === tab;
        t.classList.toggle('tx__active', selected);
        t.setAttribute('aria-selected', selected ? 'true' : 'false');
        t.setAttribute('tabindex', selected ? '0' : '-1');

        const panel = panelFor(t);
        if (panel) {
          panel.hidden = !selected;
          panel.setAttribute('aria-hidden', !selected ? 'true' : 'false');
        }
      });

      if (focus) tab.focus();

      if (updateHash) {
        const hash = tab.dataset.name;
        if (hash) {
          if (history.replaceState) {
            history.replaceState(null, '', `#${hash}`);
          } else {
            // Fallback (may scroll)
            location.hash = `#${hash}`;
          }
        }
      }
    };

    // Initial state: url hash → pre-marked active → first
    const getByHash = (h) => tabs.find((t) => t.dataset.name === h);
    const urlHash = (window.location.hash || '').slice(1);

    // Hide all first to avoid flashes
    panels.forEach((p) => { p.hidden = true; p.setAttribute('aria-hidden', 'true'); });
    tabs.forEach((t) => t.setAttribute('tabindex', '-1'));

    const preActive = tabs.find((t) => t.classList.contains('tx__active') || t.getAttribute('aria-selected') === 'true');
    const initial = (urlHash && getByHash(urlHash)) || preActive || tabs[0];
    if (initial) activate(initial, { focus: false, updateHash: !!urlHash });

    // Click to activate
    tablist.addEventListener('click', (e) => {
      const btn = e.target.closest('.tx-btn_tab');
      if (!btn || !tablist.contains(btn)) return;
      activate(btn);
    });

    // Keyboard navigation (WAI-ARIA Tabs pattern)
    tablist.addEventListener('keydown', (e) => {
      const { key } = e;
      const current = document.activeElement;
      if (!tabs.includes(current)) return;

      const i = tabs.indexOf(current);
      let next = null;

      if (key === 'ArrowRight' || key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
      else if (key === 'ArrowLeft' || key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
      else if (key === 'Home') next = tabs[0];
      else if (key === 'End') next = tabs[tabs.length - 1];
      else if (key === 'Enter' || key === ' ') {
        e.preventDefault();
        activate(current);
        return;
      } else {
        return;
      }

      e.preventDefault();
      next?.focus();
    });

    // React to back/forward hash changes
    window.addEventListener('hashchange', () => {
      const h = (window.location.hash || '').slice(1);
      const t = h && getByHash(h);
      if (t) activate(t, { focus: false, updateHash: false });
    });
  };

  document.querySelectorAll('.tx-sp_meta').forEach(initTabs);
});